export const AI_EXHIBIT_BATCH3_COPY = Object.freeze({
  en: {
    neuralNetwork: {
      eyebrow: "INSIDE A NEURAL NETWORK",
      title: "Layers transform numbers into new patterns.",
      subtitle:
        "Change two simple inputs and watch a tiny conceptual network transform them. This is not a biological brain—it is a mathematical model.",
      featureBlue: "How blue?",
      featureRound: "How round?",
      low: "Low",
      medium: "Medium",
      high: "High",
      inputs: "Inputs",
      hidden: "Learned transformations",
      output: "Output",
      node1: "blue detector",
      node2: "round detector",
      node3: "combined pattern",
      result: "Zorb probability",
      takeawayTitle: "What is happening?",
      takeaway:
        "Each layer takes numbers from the previous layer, combines them using learned weights, applies a mathematical transformation, and passes new numbers forward.",
      caveat:
        "The values and weights in this exhibit are a tiny fixed demonstration chosen for clarity. Real neural networks may contain millions or billions of learned parameters and many layers.",
      noBrain:
        "“Neural network” is a historical name. Artificial neurons are simplified mathematical units, not little biological neurons thinking inside the computer.",
      next: "See how language models generate text",
    },

    nextToken: {
      eyebrow: "NEXT-TOKEN PREDICTION",
      title: "A language model repeatedly asks: what might come next?",
      subtitle:
        "Choose a token. Then watch how adding that token changes the next probability distribution.",
      context: "Current context",
      choose: "Choose the next token",
      illustrative: "Illustrative probabilities—not output from a deployed model.",
      chosen: "You added",
      changed:
        "That token is now part of the context, so the model computes a new distribution for what could follow.",
      reset: "Start over",
      loopTitle: "The generation loop",
      loop: [
        "1 · Read the current context",
        "2 · Compute probabilities for possible next tokens",
        "3 · Select a token using a decoding strategy",
        "4 · Add it to the context and repeat",
      ],
      notJust:
        "Next-token prediction is foundational to modern autoregressive language models, but a useful AI assistant also depends on pretraining, post-training, instructions, tools, external information, and product software.",
      next: "How does this become an AI assistant?",
      first: {
        prompt: "The astronaut stepped onto the",
        options: [
          { token: "moon", probability: 71 },
          { token: "surface", probability: 14 },
          { token: "station", probability: 9 },
          { token: "sandwich", probability: 1 },
        ],
      },
      secondByToken: {
        moon: [
          { token: ".", probability: 28 },
          { token: "and", probability: 25 },
          { token: "carefully", probability: 16 },
          { token: "where", probability: 8 },
        ],
        surface: [
          { token: "of", probability: 43 },
          { token: ".", probability: 18 },
          { token: "carefully", probability: 12 },
          { token: "and", probability: 10 },
        ],
        station: [
          { token: ".", probability: 31 },
          { token: "platform", probability: 21 },
          { token: "and", probability: 17 },
          { token: "deck", probability: 9 },
        ],
        sandwich: [
          { token: ".", probability: 34 },
          { token: "and", probability: 19 },
          { token: "by", probability: 10 },
          { token: "before", probability: 8 },
        ],
      },
    },

    assistant: {
      eyebrow: "FROM LLM TO AI ASSISTANT",
      title: "The model is only part of the product.",
      subtitle:
        "Tap a task to see what other pieces may work with a large language model.",
      taskPrompt: "What do you want the assistant to do?",
      tasks: [
        {
          id: "math",
          icon: "🧮",
          title: "Calculate 347 × 89",
          modelRole: "Understand the request and decide that exact arithmetic is needed.",
          extra: "Calculator or code tool",
          result: "Use the tool result, then explain it clearly.",
          note: "A language model can attempt arithmetic directly, but a calculator tool can provide a more reliable exact result.",
        },
        {
          id: "current",
          icon: "🌐",
          title: "What is the weather today?",
          modelRole: "Understand the location and what current information is being requested.",
          extra: "Live weather / search service",
          result: "Read current data and turn it into a useful answer.",
          note: "A pretrained model does not automatically know today's changing weather.",
        },
        {
          id: "document",
          icon: "📄",
          title: "Summarize this 20-page report",
          modelRole: "Understand the user's question and synthesize relevant passages.",
          extra: "Document context / retrieval",
          result: "Ground the answer in the supplied report.",
          note: "Giving the model relevant source material can make an answer more grounded and useful.",
        },
        {
          id: "image",
          icon: "🖼️",
          title: "What is happening in this image?",
          modelRole: "Combine language with learned visual representations.",
          extra: "Multimodal vision capability",
          result: "Describe or answer questions about relevant visual content.",
          note: "Modern multimodal systems can process combinations of text, images, audio, and sometimes video.",
        },
        {
          id: "code",
          icon: "💻",
          title: "Analyze my dataset",
          modelRole: "Plan the analysis and decide what computation is required.",
          extra: "Code execution / data tools",
          result: "Inspect the computed result and explain what it means.",
          note: "Tool use lets the product perform operations that should not rely only on generated text.",
        },
      ],
      pipeline: ["Pretraining", "Post-training", "General-purpose model", "Tools + information", "Assistant"],
      pieces: {
        pretraining: {
          title: "Pretraining",
          text: "The model learns broad statistical patterns from very large collections of data, often through objectives such as predicting missing or next tokens.",
        },
        posttraining: {
          title: "Post-training",
          text: "Instruction examples, preference feedback, safety techniques, and other methods shape how the model follows requests and behaves.",
        },
        tools: {
          title: "Tools + information",
          text: "Search, calculators, documents, databases, code execution, sensors, or other software can extend what the product can do.",
        },
      },
      key:
        "What looks like “one AI” may actually be a model plus instructions, software, tools, external data, and an interface working together.",
      next: "Why can this still go wrong?",
    },

    mistakes: {
      eyebrow: "WHY AI GETS THINGS WRONG",
      title: "A mistake can come from many places.",
      subtitle:
        "Read a scenario and choose the most likely explanation. The goal is not a perfect score—the explanation is the lesson.",
      scenario: "Scenario",
      choose: "What is the main issue here?",
      reveal: "Reveal explanation",
      another: "Another scenario",
      scenarios: [
        {
          id: "unverified_citation",
          text:
            "An AI assistant confidently cites a scientific paper with a realistic title—but the paper does not exist.",
          choices: [
            "Generation without verification",
            "Distribution shift",
            "Not enough computing power",
          ],
          answer: "Generation without verification",
          explanation:
            "A generative model can produce plausible-looking text without independently checking whether every claim or citation is real. Retrieval, tools, citations, and verification can help—but do not make errors impossible.",
        },
        {
          id: "distribution_shift_snow",
          text:
            "An image model performs very well during testing in sunny weather, then its accuracy drops when cameras are used during heavy snow.",
          choices: [
            "Distribution shift",
            "The model became conscious",
            "Too many output choices",
          ],
          answer: "Distribution shift",
          explanation:
            "The conditions in deployment differ from the conditions represented in training and testing. Models can become less reliable when the real world changes.",
        },
        {
          id: "historical_bias_hiring",
          text:
            "A hiring model is trained on years of past decisions from an organization whose historical choices contained unfair patterns.",
          choices: [
            "Skewed data and historical bias",
            "The computer needs a faster GPU",
            "The prompt is too short",
          ],
          answer: "Skewed data and historical bias",
          explanation:
            "Learning from historical examples can reproduce or amplify patterns that people do not want to preserve. Data, labels, objectives, evaluation, and deployment all matter.",
        },
        {
          id: "wrong_objective_loophole",
          text:
            "A game-playing agent earns points by exploiting a loophole in the scoring system instead of playing the way its designers intended.",
          choices: [
            "Wrong or incomplete objective",
            "The training data is bilingual",
            "The network has too few colors",
          ],
          answer: "Wrong or incomplete objective",
          explanation:
            "A system can optimize exactly what was measured while missing what people actually meant. The objective is often only a proxy for the real goal.",
        },
        {
          id: "ambiguous_bank",
          text:
            "A user asks, “Tell me about the bank,” but gives no clue whether they mean a financial institution or the side of a river.",
          choices: [
            "Ambiguous context",
            "Hardware failure",
            "Model compression",
          ],
          answer: "Ambiguous context",
          explanation:
            "Several interpretations fit the same words. More context—or a clarifying question—may be needed before a reliable answer is possible.",
        },
      ],
      confidence:
        "Confidence is not correctness. For consequential decisions, outputs need appropriate evidence, evaluation, safeguards, and human accountability.",
      causes: [
        "Incomplete or skewed data",
        "New situations / distribution shift",
        "Ambiguous evidence or instructions",
        "Wrong or incomplete objective",
        "Generation without verification",
        "Human + system interaction",
      ],
    },
  },

  es: {
    neuralNetwork: {
      eyebrow: "DENTRO DE UNA RED NEURONAL",
      title: "Las capas transforman números en nuevos patrones.",
      subtitle:
        "Cambia dos entradas sencillas y observa cómo una pequeña red conceptual las transforma. No es un cerebro biológico; es un modelo matemático.",
      featureBlue: "¿Qué tan azul?",
      featureRound: "¿Qué tan redondo?",
      low: "Bajo",
      medium: "Medio",
      high: "Alto",
      inputs: "Entradas",
      hidden: "Transformaciones aprendidas",
      output: "Salida",
      node1: "detector de azul",
      node2: "detector de redondez",
      node3: "patrón combinado",
      result: "Probabilidad de Zorb",
      takeawayTitle: "¿Qué está ocurriendo?",
      takeaway:
        "Cada capa recibe números de la capa anterior, los combina usando pesos aprendidos, aplica una transformación matemática y pasa nuevos números hacia adelante.",
      caveat:
        "Los valores y pesos de esta exhibición son una demostración pequeña y fija elegida para mayor claridad. Las redes reales pueden tener millones o miles de millones de parámetros.",
      noBrain:
        "“Red neuronal” es un nombre histórico. Las neuronas artificiales son unidades matemáticas simplificadas, no pequeñas neuronas biológicas pensando dentro de la computadora.",
      next: "Mira cómo los modelos de lenguaje generan texto",
    },

    nextToken: {
      eyebrow: "PREDICCIÓN DEL SIGUIENTE TOKEN",
      title: "Un modelo de lenguaje pregunta repetidamente: ¿qué podría venir después?",
      subtitle:
        "Elige un token y observa cómo añadirlo cambia la siguiente distribución de probabilidades.",
      context: "Contexto actual",
      choose: "Elige el siguiente token",
      illustrative: "Probabilidades ilustrativas; no son la salida de un modelo desplegado.",
      chosen: "Añadiste",
      changed:
        "Ese token ahora forma parte del contexto, así que el modelo calcula una nueva distribución para lo que podría seguir.",
      reset: "Empezar de nuevo",
      loopTitle: "El ciclo de generación",
      loop: [
        "1 · Leer el contexto actual",
        "2 · Calcular probabilidades para posibles tokens",
        "3 · Seleccionar un token mediante una estrategia",
        "4 · Añadirlo al contexto y repetir",
      ],
      notJust:
        "La predicción de tokens es fundamental para modelos autorregresivos modernos, pero un asistente útil también depende de preentrenamiento, postentrenamiento, instrucciones, herramientas, información externa y software.",
      next: "¿Cómo se convierte esto en un asistente de IA?",
      first: {
        prompt: "El astronauta pisó la",
        options: [
          { token: "luna", probability: 71 },
          { token: "superficie", probability: 14 },
          { token: "estación", probability: 9 },
          { token: "sandía", probability: 1 },
        ],
      },
      secondByToken: {
        luna: [
          { token: ".", probability: 28 },
          { token: "y", probability: 25 },
          { token: "cuidadosamente", probability: 16 },
          { token: "donde", probability: 8 },
        ],
        superficie: [
          { token: "de", probability: 43 },
          { token: ".", probability: 18 },
          { token: "cuidadosamente", probability: 12 },
          { token: "y", probability: 10 },
        ],
        estación: [
          { token: ".", probability: 31 },
          { token: "espacial", probability: 21 },
          { token: "y", probability: 17 },
          { token: "orbital", probability: 9 },
        ],
        sandía: [
          { token: ".", probability: 34 },
          { token: "y", probability: 19 },
          { token: "antes", probability: 10 },
          { token: "mientras", probability: 8 },
        ],
      },
    },

    assistant: {
      eyebrow: "DEL LLM AL ASISTENTE DE IA",
      title: "El modelo es solo una parte del producto.",
      subtitle:
        "Toca una tarea para ver qué otras piezas pueden trabajar con un modelo grande de lenguaje.",
      taskPrompt: "¿Qué quieres que haga el asistente?",
      tasks: [
        { id: "math", icon: "🧮", title: "Calcular 347 × 89", modelRole: "Entender la petición y decidir que se necesita aritmética exacta.", extra: "Calculadora o herramienta de código", result: "Usar el resultado de la herramienta y explicarlo.", note: "Una calculadora puede ofrecer un resultado exacto más fiable que depender solo del texto generado." },
        { id: "current", icon: "🌐", title: "¿Qué tiempo hace hoy?", modelRole: "Entender la ubicación y que se solicita información actual.", extra: "Servicio meteorológico / búsqueda", result: "Leer datos actuales y convertirlos en una respuesta útil.", note: "Un modelo preentrenado no conoce automáticamente el clima cambiante de hoy." },
        { id: "document", icon: "📄", title: "Resumir este informe de 20 páginas", modelRole: "Entender la pregunta y sintetizar pasajes relevantes.", extra: "Documento / recuperación de contexto", result: "Fundamentar la respuesta en el informe suministrado.", note: "Dar al modelo material fuente relevante puede hacer la respuesta más fundamentada." },
        { id: "image", icon: "🖼️", title: "¿Qué ocurre en esta imagen?", modelRole: "Combinar lenguaje con representaciones visuales aprendidas.", extra: "Capacidad multimodal de visión", result: "Describir o responder preguntas sobre contenido visual.", note: "Los sistemas multimodales pueden procesar combinaciones de texto, imágenes, audio y a veces video." },
        { id: "code", icon: "💻", title: "Analizar mis datos", modelRole: "Planificar el análisis y decidir qué cálculo hace falta.", extra: "Ejecución de código / herramientas de datos", result: "Inspeccionar el resultado calculado y explicarlo.", note: "Las herramientas permiten realizar operaciones que no deberían depender solo de texto generado." },
      ],
      pipeline: ["Preentrenamiento", "Postentrenamiento", "Modelo general", "Herramientas + información", "Asistente"],
      pieces: {
        pretraining: { title: "Preentrenamiento", text: "El modelo aprende patrones estadísticos amplios de grandes colecciones de datos, a menudo mediante objetivos como predecir tokens." },
        posttraining: { title: "Postentrenamiento", text: "Ejemplos de instrucciones, preferencias, técnicas de seguridad y otros métodos moldean cómo responde el modelo." },
        tools: { title: "Herramientas + información", text: "Búsqueda, calculadoras, documentos, bases de datos, código, sensores u otro software pueden ampliar lo que hace el producto." },
      },
      key:
        "Lo que parece “una IA” puede ser en realidad un modelo más instrucciones, software, herramientas, datos externos y una interfaz trabajando juntos.",
      next: "¿Por qué todavía puede equivocarse?",
    },

    mistakes: {
      eyebrow: "POR QUÉ SE EQUIVOCA LA IA",
      title: "Un error puede venir de muchos lugares.",
      subtitle:
        "Lee una situación y elige la explicación más probable. La explicación importa más que la puntuación.",
      scenario: "Situación",
      choose: "¿Cuál es el problema principal?",
      reveal: "Mostrar explicación",
      another: "Otra situación",
      scenarios: [
        { id: "unverified_citation", text: "Un asistente cita con mucha confianza un artículo científico con un título realista, pero el artículo no existe.", choices: ["Generación sin verificación", "Cambio de distribución", "Falta de computación"], answer: "Generación sin verificación", explanation: "Un modelo generativo puede producir texto plausible sin comprobar de forma independiente que cada afirmación o cita sea real." },
        { id: "distribution_shift_snow", text: "Un modelo de imágenes funciona muy bien con sol, pero su precisión cae cuando las cámaras se usan durante una fuerte nevada.", choices: ["Cambio de distribución", "El modelo se volvió consciente", "Demasiadas opciones"], answer: "Cambio de distribución", explanation: "Las condiciones reales son diferentes de las representadas durante entrenamiento y pruebas." },
        { id: "historical_bias_hiring", text: "Un modelo de contratación aprende de años de decisiones anteriores de una organización que contenían patrones injustos.", choices: ["Datos sesgados y sesgo histórico", "Hace falta una GPU más rápida", "El prompt es corto"], answer: "Datos sesgados y sesgo histórico", explanation: "Aprender de ejemplos históricos puede reproducir o amplificar patrones que las personas no desean conservar." },
        { id: "wrong_objective_loophole", text: "Un agente de juego consigue puntos explotando un fallo del sistema de puntuación en vez de jugar como querían sus diseñadores.", choices: ["Objetivo incorrecto o incompleto", "Datos bilingües", "Pocos colores"], answer: "Objetivo incorrecto o incompleto", explanation: "Un sistema puede optimizar exactamente lo que se midió y aun así no hacer lo que las personas querían realmente." },
        { id: "ambiguous_bank", text: "Un usuario dice: “Háblame del banco”, sin aclarar si habla de una institución financiera o de la orilla de un río.", choices: ["Contexto ambiguo", "Fallo de hardware", "Compresión del modelo"], answer: "Contexto ambiguo", explanation: "Varias interpretaciones encajan con las mismas palabras; puede hacer falta más contexto o una pregunta aclaratoria." },
      ],
      confidence:
        "Confianza no significa corrección. En decisiones importantes hacen falta evidencia, evaluación, salvaguardas y responsabilidad humana.",
      causes: [
        "Datos incompletos o sesgados",
        "Situaciones nuevas / cambio de distribución",
        "Evidencia o instrucciones ambiguas",
        "Objetivo incorrecto o incompleto",
        "Generación sin verificación",
        "Interacción entre personas y sistema",
      ],
    },
  },
});

export function getAIExhibitBatch3Copy(language = "en") {
  return AI_EXHIBIT_BATCH3_COPY[language] || AI_EXHIBIT_BATCH3_COPY.en;
}
